/**
 * `.PAL` palettes (docs/formats/images-palettes.md, VERIFIED). Container:
 *
 * ```
 * PAL:  (container)
 *   VGA:  768 bytes, RAW — no compression header
 * ```
 *
 * 768 bytes = 256 entries of `{r, g, b}`, each a **6-bit** (0-63) VGA DAC
 * value. **Trap**: `VGA:` is the one leaf chunk in the whole family that
 * never carries a compression header, even though `VGA:` chunks inside
 * `.SCR`/`.BMP`/`.OVL` always do — reading a compression header here yields
 * a 763-byte palette and wrong colours.
 */

import { requireChunk, requireLeaf, parseChunks } from "./chunk.ts";

const ENTRY_COUNT = 256;
const CHANNELS = 3;
const RAW_LENGTH = ENTRY_COUNT * CHANNELS;

export interface Palette {
  /** Raw 6-bit (0-63) VGA DAC values, `{r, g, b}` per entry, 768 bytes. */
  readonly raw6: Uint8Array;
  /** The same values expanded to 8-bit (0-255) per channel, 768 bytes. */
  readonly rgb: Uint8Array;
}

/** Expands one 6-bit VGA DAC value to 8-bit, round-trip-preserving (not the cruder `v << 2`). */
export function expand6to8(value: number): number {
  return (value << 2) | (value >> 4);
}

/** Decodes a whole `.PAL` file's bytes into a 256-entry palette. */
export function decodePal(source: ArrayBufferLike | ArrayBufferView): Palette {
  const nodes = parseChunks(source);
  const pal = requireChunk(nodes, "PAL:", ".PAL file");
  if (pal.kind !== "container") {
    throw new Error(".PAL: top-level 'PAL:' chunk must be a container");
  }

  const raw6 = requireLeaf(pal.children, "VGA:", "PAL:");
  if (raw6.length !== RAW_LENGTH) {
    throw new Error(`.PAL: 'VGA:' chunk must be exactly ${RAW_LENGTH} bytes (256 x RGB), got ${raw6.length}`);
  }

  const rgb = new Uint8Array(RAW_LENGTH);
  for (let i = 0; i < RAW_LENGTH; i++) {
    rgb[i] = expand6to8(raw6[i]!);
  }

  return { raw6, rgb };
}

export interface ToRgbaOptions {
  /** A palette index to render fully transparent (alpha 0) instead of opaque; unset by default. */
  readonly transparentIndex?: number;
}

/** Expands indexed pixels to interleaved RGBA bytes using an 8-bit-expanded 768-byte palette. */
export function toRGBA(indexed: Uint8Array, palette: Uint8Array, options: ToRgbaOptions = {}): Uint8Array {
  if (palette.length !== RAW_LENGTH) {
    throw new Error(`toRGBA: palette must be exactly ${RAW_LENGTH} bytes (256 x RGB), got ${palette.length}`);
  }

  const out = new Uint8Array(indexed.length * 4);
  for (let i = 0; i < indexed.length; i++) {
    const index = indexed[i]!;
    const p = index * CHANNELS;
    const o = i * 4;
    out[o] = palette[p]!;
    out[o + 1] = palette[p + 1]!;
    out[o + 2] = palette[p + 2]!;
    out[o + 3] = index === options.transparentIndex ? 0 : 255;
  }
  return out;
}
